import { formatPrice } from "../../../../../../common/helpers/formatPrice";
import type { RawCart } from "../../../../../../types";
import type { TransformedCart } from "../../../types/cartApiTypes";

// This will be the return type of our new helper.
// It's a subset of the final TransformedCart.
export type TransformedFreeShippingData = Pick<
  TransformedCart,
  | "isFreeShippingAvailable"
  | "freeShippingRequirement"
  | "hasFreeShippingCoupon"
  | "hasMetMinAmount"
  | "freeShippingThreshold"
  | "formattedFreeShippingThreshold"
  | "amountNeededForFreeShipping"
  | "formattedAmountNeededForFreeShipping"
>;

/**
 * A pure helper function to transform the `free_shipping_threshold`
 * extension data from the raw cart object.
 *
 * @param cart The raw cart object from the WC Store API.
 * @returns An object containing all transformed free shipping data.
 */
export const transformFreeShippingData = (
  cart: RawCart
): TransformedFreeShippingData => {
  // --- Free Shipping Threshold Logic ---
  const rawFreeShippingData = cart.extensions?.free_shipping_threshold;

  // Extract currency info from the main cart totals for formatting
  const currencyInfo = {
    currencyMinorUnit: cart.totals.currency_minor_unit,
    currencyDecimalSeparator: cart.totals.currency_decimal_separator,
    currencyThousandSeparator: cart.totals.currency_thousand_separator,
    currencyPrefix: cart.totals.currency_prefix,
    currencySuffix: cart.totals.currency_suffix,
  };

  const isFreeShippingAvailable =
    rawFreeShippingData?.is_free_shipping_available ?? false;
  const freeShippingRequirement =
    rawFreeShippingData?.requirement ?? "not_configured";
  const hasFreeShippingCoupon =
    rawFreeShippingData?.has_free_shipping_coupon ?? false;
  const hasMetMinAmount = rawFreeShippingData?.has_met_min_amount ?? false;

  const rawThreshold = rawFreeShippingData?.threshold ?? null;
  const rawAmountNeeded = rawFreeShippingData?.amount_needed ?? null;

  const minorUnit = cart.totals.currency_minor_unit || 2;
  const divisor = Math.pow(10, minorUnit);

  let freeShippingThreshold: number | null = null;
  let formattedFreeShippingThreshold: string | null = null;
  let amountNeededForFreeShipping: number | null = null;
  let formattedAmountNeededForFreeShipping: string | null = null;

  if (rawThreshold !== null) {
    freeShippingThreshold = rawThreshold / divisor;
    formattedFreeShippingThreshold = formatPrice(
      String(rawThreshold),
      currencyInfo
    );
  }
  if (rawAmountNeeded !== null) {
    amountNeededForFreeShipping = rawAmountNeeded / divisor;
    formattedAmountNeededForFreeShipping = formatPrice(
      String(rawAmountNeeded),
      currencyInfo
    );
  }

  // --- Return Transformed Data ---
  return {
    isFreeShippingAvailable,
    freeShippingRequirement,
    hasFreeShippingCoupon,
    hasMetMinAmount,
    freeShippingThreshold,
    formattedFreeShippingThreshold,
    amountNeededForFreeShipping,
    formattedAmountNeededForFreeShipping,
  };
};
